import { parse as llParse, stringify as llStringify, isLosslessNumber } from "lossless-json";
import * as yaml from "js-yaml";

export interface ParseError {
  message: string;
  line?: number;
  column?: number;
  lineText?: string;
}
export type Kind = "json" | "yaml";
export interface Options {
  indent: 2 | 4 | "tab";
  sortKeys: boolean;
}
export type Result =
  | { ok: true; output: string; kind: Kind }
  | { ok: false; error: ParseError; repairable: boolean };

function posToLineCol(text: string, pos: number) {
  const before = text.slice(0, pos);
  const line = before.split("\n").length;
  const column = pos - before.lastIndexOf("\n");
  return { line, column, lineText: text.split("\n")[line - 1] ?? "" };
}

function jsonError(text: string, e: unknown): ParseError {
  const message = e instanceof Error ? e.message : "Invalid JSON";
  const m = /position (\d+)/.exec(message);
  if (!m) return { message };
  return { message: message.replace(/\s*at position \d+/, ""), ...posToLineCol(text, Number(m[1])) };
}

function yamlError(text: string, e: unknown): ParseError {
  const err = e as { reason?: string; message?: string; mark?: { line: number; column: number } };
  const message = err.reason ?? err.message ?? "Invalid YAML";
  if (!err.mark) return { message };
  const line = err.mark.line + 1;
  return { message, line, column: err.mark.column + 1, lineText: text.split("\n")[line - 1] ?? "" };
}

/** Parses JSON without changing numbers (big integers, 1.10 stay exactly as written). */
export function parseJson(text: string): unknown {
  return llParse(text);
}

function sortDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortDeep);
  if (v && typeof v === "object" && !isLosslessNumber(v)) {
    const o = v as Record<string, unknown>;
    return Object.fromEntries(Object.keys(o).sort().map(k => [k, sortDeep(o[k])]));
  }
  return v;
}

/** Plain-JS copy for YAML output; big numbers become BigInt/number only if safe. */
function toPlain(v: unknown): unknown {
  if (isLosslessNumber(v)) {
    const s = v.toString();
    if (/^-?\d+$/.test(s) && !Number.isSafeInteger(Number(s))) return BigInt(s);
    return Number(s);
  }
  if (Array.isArray(v)) return v.map(toPlain);
  if (v && typeof v === "object") {
    return Object.fromEntries(Object.entries(v as object).map(([k, x]) => [k, toPlain(x)]));
  }
  return v;
}

const indentArg = (i: Options["indent"]) => (i === "tab" ? "\t" : i);

function tryYaml(text: string): { ok: true; value: unknown } | { ok: false; error: ParseError } {
  try {
    return { ok: true, value: yaml.load(text) };
  } catch (e) {
    return { ok: false, error: yamlError(text, e) };
  }
}

export function detectKind(text: string): Kind {
  const t = text.trim();
  if (/^[\[{"]/.test(t)) return "json";
  try {
    llParse(t);
    return "json";
  } catch {
    /* fall through */
  }
  const y = tryYaml(t);
  return y.ok && y.value !== null && typeof y.value === "object" ? "yaml" : "json";
}

export type Action = "format" | "minify" | "convert";

/**
 * format: pretty-print (YAML input is converted to JSON)
 * minify: compact JSON
 * convert: JSON -> YAML, YAML -> JSON
 */
export function run(text: string, action: Action, o: Options): Result {
  if (!text.trim()) return { ok: false, error: { message: "Paste some JSON or YAML first." }, repairable: false };
  const kind = detectKind(text);

  if (kind === "yaml") {
    const y = tryYaml(text);
    if (!y.ok) return { ok: false, error: y.error, repairable: false };
    const value = o.sortKeys ? sortDeep(y.value) : y.value;
    const json = JSON.stringify(value, (_k, v) => (typeof v === "bigint" ? v.toString() : v), action === "minify" ? undefined : indentArg(o.indent));
    return { ok: true, output: json ?? "null", kind };
  }

  let value: unknown;
  try {
    value = llParse(text);
  } catch (e) {
    return { ok: false, error: jsonError(text, e), repairable: repair(text) !== null };
  }
  if (o.sortKeys) value = sortDeep(value);

  if (action === "convert") {
    return { ok: true, output: yaml.dump(toPlain(value), { indent: o.indent === "tab" ? 2 : o.indent, lineWidth: -1 }), kind };
  }
  const out = llStringify(value, undefined, action === "minify" ? undefined : indentArg(o.indent));
  return { ok: true, output: out ?? "null", kind };
}

/**
 * Text-level repair for common near-JSON: comments, single quotes, unquoted keys
 * and trailing commas. Works on the text so numbers are never re-rendered.
 * Returns null when the result still is not valid JSON.
 */
export function repair(text: string): string | null {
  let out = "";
  let i = 0;
  const n = text.length;
  const isIdStart = (c: string) => /[A-Za-z_$]/.test(c);
  while (i < n) {
    const c = text[i];
    if (c === '"' || c === "'") {
      const q = c;
      let s = "";
      i++;
      while (i < n && text[i] !== q) {
        if (text[i] === "\\" && i + 1 < n) {
          const nx = text[i + 1];
          s += nx === "'" ? "'" : "\\" + nx;
          i += 2;
          continue;
        }
        s += text[i] === '"' ? '\\"' : text[i];
        i++;
      }
      i++;
      out += '"' + s + '"';
      continue;
    }
    if (c === "/" && text[i + 1] === "/") {
      while (i < n && text[i] !== "\n") i++;
      continue;
    }
    if (c === "/" && text[i + 1] === "*") {
      i += 2;
      while (i < n && !(text[i] === "*" && text[i + 1] === "/")) i++;
      i += 2;
      continue;
    }
    if (c === ",") {
      let j = i + 1;
      while (j < n && /\s/.test(text[j])) j++;
      if (text[j] === "}" || text[j] === "]") {
        i++;
        continue;
      }
    }
    if (isIdStart(c)) {
      let j = i;
      while (j < n && /[\w$]/.test(text[j])) j++;
      const word = text.slice(i, j);
      let k = j;
      while (k < n && /\s/.test(text[k])) k++;
      if (text[k] === ":" && !["true", "false", "null"].includes(word)) out += '"' + word + '"';
      else out += word;
      i = j;
      continue;
    }
    out += c;
    i++;
  }
  try {
    llParse(out);
    return out;
  } catch {
    return null;
  }
}
